import { useState, useMemo } from 'react';
import { initialArtifactState, } from './types';
export const useArtifactState = () => {
    const [artifactState, setArtifactState] = useState(initialArtifactState);
    const artifactActions = useMemo(() => ({
        updateLoaded: (artifactId, artifact) => {
            setArtifactState(prev => {
                const newLoaded = new Map(prev.loaded);
                newLoaded.set(artifactId, artifact);
                return {
                    ...prev,
                    loaded: newLoaded
                };
            });
        },
        removeLoaded: (artifactId) => {
            setArtifactState(prev => {
                const newLoaded = new Map(prev.loaded);
                newLoaded.delete(artifactId);
                return {
                    ...prev,
                    loaded: newLoaded
                };
            });
        },
        updateMetadataCache: (artifactId, cache) => {
            setArtifactState(prev => {
                const newCache = new Map(prev.metadataCache);
                newCache.set(artifactId, cache);
                return {
                    ...prev,
                    metadataCache: newCache
                };
            });
        },
        getMetadataCache: (artifactId) => {
            return artifactState.metadataCache.get(artifactId);
        },
        updateControls: (artifactId, controls) => {
            setArtifactState(prev => {
                const newControls = new Map(prev.controls);
                newControls.set(artifactId, controls);
                return { ...prev, controls: newControls };
            });
        },
        // patchControls merges a partial update against the latest state inside the
        // updater, so two control writes for the same artifact in one tick (e.g. a
        // scope restoring opacity and monochrome together) don't clobber each other
        // the way a read-snapshot-then-replace write would.
        patchControls: (artifactId, partial) => {
            setArtifactState(prev => {
                const current = prev.controls.get(artifactId) || { opacity: 1.0 };
                const newControls = new Map(prev.controls);
                newControls.set(artifactId, { ...current, ...partial });
                return { ...prev, controls: newControls };
            });
        },
        // initControlsIfAbsent sets default controls only if none exist, checking the
        // LIVE state inside the updater. Loaders must use this rather than a
        // has()-then-updateControls on a captured snapshot: an async load can finish
        // AFTER a scope restore has already set this artifact's controls, and a stale
        // snapshot would wrongly think them absent and clobber them back to defaults.
        initControlsIfAbsent: (artifactId, defaults) => {
            setArtifactState(prev => {
                if (prev.controls.has(artifactId))
                    return prev;
                const newControls = new Map(prev.controls);
                newControls.set(artifactId, defaults);
                return { ...prev, controls: newControls };
            });
        },
        updateVisibility: (artifactId, visible) => {
            setArtifactState(prev => {
                const newVisibility = new Map(prev.visibility);
                newVisibility.set(artifactId, visible);
                return { ...prev, visibility: newVisibility };
            });
        },
        setLoading: (artifactId, loading) => {
            setArtifactState(prev => {
                const newLoading = new Map(prev.loading);
                newLoading.set(artifactId, loading);
                return { ...prev, loading: newLoading };
            });
        },
        incrementRetryCount: (artifactId) => {
            setArtifactState(prev => {
                const newRetryCount = new Map(prev.retryCount);
                const currentCount = newRetryCount.get(artifactId) || 0;
                newRetryCount.set(artifactId, currentCount + 1);
                return { ...prev, retryCount: newRetryCount };
            });
        },
        resetRetryCount: (artifactId) => {
            setArtifactState(prev => {
                const newRetryCount = new Map(prev.retryCount);
                newRetryCount.delete(artifactId);
                return { ...prev, retryCount: newRetryCount };
            });
        },
        addScreenOverlay: (overlay) => {
            setArtifactState(prev => ({
                ...prev,
                activeOverlays: {
                    ...prev.activeOverlays,
                    screen: [...prev.activeOverlays.screen, overlay]
                }
            }));
        },
        removeScreenOverlay: (overlayId) => {
            setArtifactState(prev => ({
                ...prev,
                activeOverlays: {
                    ...prev.activeOverlays,
                    screen: prev.activeOverlays.screen.filter(overlay => overlay.id !== overlayId)
                }
            }));
        },
        addInfoPanel: (panel) => {
            setArtifactState(prev => ({
                ...prev,
                activeOverlays: {
                    ...prev.activeOverlays,
                    info: [...prev.activeOverlays.info, panel]
                }
            }));
        },
        removeInfoPanel: (panelId) => {
            setArtifactState(prev => ({
                ...prev,
                activeOverlays: {
                    ...prev.activeOverlays,
                    info: prev.activeOverlays.info.filter(panel => panel.id !== panelId)
                }
            }));
        },
        clearAll: () => {
            setArtifactState(initialArtifactState);
        }
    }), [artifactState.metadataCache]);
    return {
        artifactState,
        artifactActions,
        setArtifactState,
    };
};
